import { useMemo, useState } from 'react';

import Button, { ButtonStyleType } from '@/components/common/Button/Button';
import { UpliftIndex, UpliftIndexTypePascalCaseEnum, UpliftIndexRate } from '@/graphql/contracts/_generated_/contracts';
import RateModal from './RateModal';
import AgConfirmationModal from '@/components/common/ModalWindow/AgConfirmationModal';
import { useUpdateUpliftIndexRate } from '../hooks/useUpdateUpliftIndexRate';
import { useDeleteUpliftIndexRates } from '../hooks/useDeleteUpliftIndexRates';
import { GetUpliftIndexesVariables } from '@/graphql/contracts/_generated_/getUpliftIndexes.query';

interface Params {
  data: { id: string; effectiveDate: string; rate: string };
  selectedIndex: UpliftIndex;
  isAdminOrFullAccessRole: boolean;
  regionName?: string | null;
}

export default function RateActionsCellRenderer(props: any) {
  const { data, selectedIndex, isAdminOrFullAccessRole, regionName } = props as Params;

  const [isUpdateOpen, setIsUpdateOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);

  const isFuture = useMemo(() => {
    try {
      return new Date(data.effectiveDate).getTime() > Date.now();
    } catch {
      return false;
    }
  }, [data.effectiveDate]);

  const canEdit = isAdminOrFullAccessRole
    && selectedIndex?.type === UpliftIndexTypePascalCaseEnum.Custom
    && isFuture;

  const [updateRate, { loading: updating }] = useUpdateUpliftIndexRate({
    region: regionName,
    includeRates: true,
  } as GetUpliftIndexesVariables);
  const [deleteRates, { loading: deleting }] = useDeleteUpliftIndexRates({
    region: regionName,
    includeRates: true,
  } as GetUpliftIndexesVariables);

  if (!canEdit) return null;

  const currentRate: UpliftIndexRate = {
    id: data.id as any,
    effectiveDate: data.effectiveDate as any,
    rate: data.rate as any,
    created: null as any,
    updated: null as any,
    __typename: 'UpliftIndexRate',
  };

  return (
    <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
      <Button buttonStyleType={ButtonStyleType.SECONDARY} onClick={() => setIsUpdateOpen(true)}>
        Update
      </Button>
      <Button buttonStyleType={ButtonStyleType.SECONDARY} onClick={() => setIsDeleteOpen(true)}>
        Delete
      </Button>

      {isUpdateOpen && (
        <RateModal
          isOpen={true}
          onClose={() => setIsUpdateOpen(false)}
          title="Update Rate"
          initialRate={currentRate}
          loading={updating}
          onSubmit={async ({ rate, effectiveDate }) => {
            await updateRate({
              variables: {
                input: {
                  upliftIndexId: selectedIndex.id!,
                  upliftIndexRateId: data.id,
                  rate: rate / 100, // Convert back to decimal fraction
                  effectiveDate,
                },
              },
            });
          }}
        />
      )}

      {isDeleteOpen && (
        <AgConfirmationModal
          isOpen={true}
          onClose={() => setIsDeleteOpen(false)}
          title="Delete Rate"
          confirmText="Delete"
          isConfirmButtonDisabled={deleting}
          onConfirm={async () => {
            await deleteRates({
              variables: {
                upliftIndexId: selectedIndex.id!,
                upliftIndexRateIds: [data.id],
              },
            });
          }}
        >
          <span>Deleting this rate may significantly impact contract lines that reference it.</span>
          <br />
          <span>Are you sure you want to proceed?</span>
        </AgConfirmationModal>
      )}
    </div>
  );
}
